<template>
  <liste-template>
    <template #header>
      <h1 class="title is-size-3">{{ $t('listeLignesFacture.title')}}</h1>
      <breadcrumb :breadcrumb="breadcrumb"/>
    </template>
    <template #bar-left>
      <search-bar
        @recherche="recherche">
      </search-bar>
    </template>
    <template #bar-right>
      <div class="column is-narrow">
        <exportExcelButton
          :perm="constantes.COMMANDES.exporter"
          :query="exportExcelQuery"
          :secondaryExports="secondaryActions"/>
      </div>
      <parametrage-table
        :reference-table="constantes.REFERENCE_TABLE_LISTE_LIGNES_FACTURE"
        @endLoadParamtre="startArrayRendering = true">
      </parametrage-table>
    </template>
    <template #content>
      <v-server-table url=""
        :columns="getColumns"
        :options="options"
        @loaded="finLoading"
        @loading="debutLoading"
        :ref="constantes.REFERENCE_TABLE_LISTE_LIGNES_FACTURE"
        v-if="startArrayRendering && mountedLoadingEnd">
        <template slot='afterTable'>
          <table-items-in-view
            :perPage="resultPerPage"
            @input="$refs[constantes.REFERENCE_TABLE_LISTE_LIGNES_FACTURE].setLimit($event)" />
        </template>
        <template slot='appendBody'>
          <table-footer-totaux
            ref="tableTotaux"
            :columns="getColumns"
            :target="targetTotal"
            :items="lignesFacture">
          </table-footer-totaux>
        </template>
        <div slot="codeDcf" slot-scope="props">
          <router-link-blank :to="{ name: 'c.bodac.factures',
                      params: { id: props.row.devisCommandeId }}"
                            v-if="!props.row.interne"
                            :message="props.row.codeDcf">
          </router-link-blank>
          <router-link-blank :to="{ name: 'c.bodac.factures-venteInterne',
                      params: { id: props.row.devisCommandeId }}"
                            v-else
                            :message="props.row.codeDcf">
          </router-link-blank>
        </div>
        <div slot="lignesCommandeRegroupementLibelle" slot-scope="props">
          {{ props.row.lignesCommandeRegroupementLibelle }}
        </div>
        <div slot="libelle" slot-scope="props">
          {{ props.row.libelle }}
        </div>
        <div slot="dateDevisCommande" slot-scope="props">
          {{ func.formatDate(props.row.dateDevisCommande) }}
        </div>
        <div slot="montantHtDcf" slot-scope="props" class="is-pulled-right"
            :class="{ 'textDecorationLineThrough':
              props.row.etatDcf === constantes.ETAT_CLOTURER
              || props.row.etatDcf === constantes.ETAT_SANSSUITE }">
          {{ props.row.montantHtDcf | formatDevise }}
        </div>
        <div slot="totalHtAvecLigneRemise" slot-scope="props" class="is-pulled-right"
            :class="{ 'textDecorationLineThrough':
              props.row.etatDcf === constantes.ETAT_CLOTURER
              || props.row.etatDcf === constantes.ETAT_SANSSUITE }">
          {{ props.row.totalHtAvecLigneRemise | formatDevise }}
        </div>
        <div slot="datePremierAffichage" slot-scope="props">
          {{ func.formatDate(props.row.datePremierAffichage) }}
        </div>
        <div slot="listeIntervenants" slot-scope="props">
          <router-link-blank
            v-for="(client, index) in props.row.clientsNomCommerciauxIds"
            :key="index"
            v-bind="getRouteClient(client)">
          </router-link-blank>
        </div>
        <div slot="etatDcf" slot-scope="props">
          {{ getEtatLibelle(props.row.etatDcf) }}
        </div>
        <div slot="sousTypeDcf" slot-scope="props">
          {{ getSousTypeLibelle(props.row.sousTypeDcf) }}
        </div>
        <div slot="typeProduit" slot-scope="props">
          {{ $t(getLibelleCategorieProduit(props.row.typeProduit)) }}
        </div>
        <div slot="dateDebut" slot-scope="props">
          {{ func.formatDate(props.row.dateDebut) }}
        </div>
        <div slot="dateFin" slot-scope="props">
          {{ func.formatDate(props.row.dateFin) }}
        </div>
        <div slot="edit" slot-scope="props">
          <router-link-blank :to="{ name: 'm.bodac.factures-edition',
                      params: { id: props.row.devisCommandeId }}"
                            v-if="!props.row.interne"
                            icon="pencil-box-outline" typeIcon="is-success">
          </router-link-blank>
          <b-icon  icon="pencil-box-outline"
                  v-else
                  class="text-access-no-permission">
          </b-icon>
        </div>
      </v-server-table>
    </template>
  </liste-template>
</template>
<script>

import { PARAMETRES_INTERNE_KEYS } from '@/store/modules/parametres-mutation-type';
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import { COMMANDES } from '@/configuration/permissionsBodacConf';
import { SET_DETECT_CHANGE_SOCIETE } from '@/store/modules/societe-mutation-type';
import subscribeMutation from '@/services/store/subscribeMutation';
import updateCritereWithParametreInterne from '@/services/dcf/updateCritereWithParametreInterne';
import { SET_CRITERE } from '@/store/modules/bodac/factureParLigne-mutation-type';
import {
  ETAT_SANSSUITE,
  ETAT_CLOTURER,
  TYPE_FACTURE,
} from '@/configuration/devisConf';
import { get as getProduit } from '@/api/produit/index';
import exportExcelButton from '@/components/general/button/exportExcelButton';
import messageErreur from '@/services/general/messageErreur';
import { COMMERCIAL } from '@/configuration/parametrage/secteurConf';
import getSecteursIdByUser, { getSecteursByIds } from '@/services/commun/secteur/getSecteurs';
import searchBar from '@/components/bodac/lignesFacture/searchBar';
import TableItemsInView from '@/components/general/TableItemsInView';
import routerLinkBlank from '@/components/general/routerLinkBlank';
import {
  NO_PER_PAGE_VALUE,
  RESULTS_PER_PAGE,
  SORT_ICON,
  TEXTS,
} from '@/configuration/clientTableConf';
import { FACTURE_SOUS_TYPE, FACTURE_ETAT, EXPORT_EXCEL_LIGNE_FACTURE } from '@/configuration/facture/factureConf';
import checkPeriodeAndFormat from '@/services/dcf/checkPeriodeAndFormat';
import tagInputToArray from '@/services/general/filters/tagInputToArray';
import { CATEGORIE_PRODUIT } from '@/configuration/catalogue/produitConf';
import Breadcrumb from '@/components/general/breadcrumb';
import listeTemplate from '@/components/general/template/listeTemplate';
import tableFooterTotaux from '@/components/general/tableFooterTotaux';
import { REFERENCE_TABLE_LISTE_LIGNES_FACTURE } from '@/configuration/general/referenceTableConf';
import parametrageTable from '@/components/general/parametrageTable';
import formatDate from '@/services/general/formatDate';

const Permissions = createNamespacedHelpers('permissions');
const LignesFacture = createNamespacedHelpers('factureParLigne');
const Parametres = createNamespacedHelpers('parametres');
const Societe = createNamespacedHelpers('societe');
const User = createNamespacedHelpers('user');

export default {
  name: 'l_LignesFactureContainer',
  data() {
    return {
      constantes: {
        COMMANDES,
        ETAT_SANSSUITE,
        ETAT_CLOTURER,
        EXPORT_EXCEL_LIGNE_FACTURE,
        REFERENCE_TABLE_LISTE_LIGNES_FACTURE,
      },
      resultPerPage: RESULTS_PER_PAGE,
      startArrayRendering: false,
      mountedLoadingEnd: false,
      func: {
        formatDate,
      },
    };
  },
  props: {
    produit: {
      type: Object,
      required: false,
      default: null,
    },
    produitId: {
      type: String,
      required: false,
    },
  },
  components: {
    searchBar,
    TableItemsInView,
    routerLinkBlank,
    Breadcrumb,
    listeTemplate,
    tableFooterTotaux,
    parametrageTable,
    exportExcelButton,
  },
  computed: {
    ...LignesFacture.mapGetters({
      lignesFacture: 'items',
      total: 'totalCount',
      criteresRecherche: 'criteresRecherche',
      lastPayload: 'lastPayload',
    }),
    ...Parametres.mapGetters({
      parametresInterne: 'parametresInterne',
    }),
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
      agencesSelectionnees: 'agenceActive',
    }),
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    ...User.mapGetters({
      user: 'user',
    }),
    breadcrumb() {
      return [
        { title: this.$i18n.t('listeFactures.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$i18n.t('listeLignesFacture.title'), route: { name: 'l.bodac.factures-lignes' }, isActive: true },
      ];
    },
    targetTotal() {
      return ['totalHtAvecLigneRemise', 'montantHtDcf'];
    },
    getHeadings() {
      return this.columns.reduce((old, a) => ({ ...old, [a.name]: this.$i18n.t(a.text) }), {});
    },
    getSortable() {
      return this.columns.filter(c => c.sortable).map(c => c.name);
    },
    getColumns() {
      return this.columns.filter(c => c.show).map(c => c.name);
    },
    options() {
      return {
        sortIcon: SORT_ICON,
        headings: this.getHeadings,
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: this.resultPerPage,
        texts: TEXTS,
        sortable: this.getSortable,
        columnsClasses: {
          listeIntervenants: 'ellipsis colonneListeIntervenants',
        },
        rowClassCallback: ((row) => {
          return row.etatDcf === ETAT_CLOTURER || row.etatDcf === ETAT_SANSSUITE ? 'textDecorationLineThrough' : '';
        }),
        requestFunction: (async (data) => {
          if (!data.query) data.query = cloneDeep(this.criteresRecherche);
          await updateCritereWithParametreInterne(data.query);
          this.gestionDataLoadLignesFacture(data);
          return this.actionsLoadLignesFactures(data);
        }),
        responseAdapter: (() => {
          return {
            data: this.lignesFacture,
            count: this.total,
          };
        }),
      };
    },
    exportLvc() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.exportLvc.key);
    },
    moduleSecteurActive() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.moduleSecteur.key);
    },
    columns() {
      return [
        {
          name: 'codeDcf', show: true, text: 'listeLignesFacture.tabs.codeCommande', sortable: true,
        },
        {
          name: 'lignesCommandeRegroupementLibelle', show: true, text: 'listeLignesFacture.tabs.patrimoine', sortable: true,
        },
        {
          name: 'libelle', show: true, text: 'listeLignesFacture.tabs.produit', sortable: true,
        },
        {
          name: 'dateDevisCommande', show: true, text: 'listeLignesFacture.tabs.dateCommande', sortable: true,
        },
        {
          name: 'sousTypeDcf', show: true, text: 'listeLignesFacture.tabs.sousType', sortable: false,
        },
        {
          name: 'nomCommercial', show: true, text: 'listeLignesFacture.tabs.commercial', sortable: false,
        },
        {
          name: 'listeIntervenants', show: true, text: 'listeLignesFacture.tabs.clients', sortable: false,
        },
        {
          name: 'etatDcf', show: true, text: 'listeLignesFacture.tabs.etat', sortable: true,
        },
        {
          name: 'datePremierAffichage', show: true, text: 'listeLignesFacture.tabs.dateCampagne', sortable: true,
        },
        {
          name: 'dateDebut', show: true, text: 'listeLignesFacture.tabs.dateDebut', sortable: false,
        },
        {
          name: 'dateFin', show: true, text: 'listeLignesFacture.tabs.dateFin', sortable: false,
        },
        {
          name: 'codeDossier', show: true, text: 'listeLignesFacture.tabs.codeDossier', sortable: true,
        },
        {
          name: 'totalHtAvecLigneRemise', show: true, text: 'listeLignesFacture.tabs.totalHt', sortable: false,
        },
        {
          name: 'montantHtDcf', show: true, text: 'listeLignesFacture.tabs.montantHt', sortable: true,
        },
        {
          name: 'typeProduit', show: !this.produit, text: 'listeLignesFacture.tabs.typeProduit', sortable: false,
        },
        {
          name: 'edit', show: !this.produit, text: '', sortable: false,
        },
      ];
    },
    exportExcelQuery() {
      return {
        typeDonnee: this.constantes.EXPORT_EXCEL_LIGNE_FACTURE,
        typeExport: null,
        filtres: this.lastPayload,
      };
    },
    secondaryActions() {
      const actionList = [];
      if (this.exportLvc) {
        actionList.push({
          libelle: this.$t('listeLignesFacture.actions.exportLvc'),
          query: {
            typeDonnee: this.constantes.EXPORT_EXCEL_LIGNE_FACTURE,
            typeExport: 'exportlvc',
            filtres: this.lastPayload,
          },
        });
      }
      return actionList;
    },
  },
  methods: {
    ...LignesFacture.mapMutations({
      setCritereRecherche: SET_CRITERE,
    }),
    ...LignesFacture.mapActions({
      actionsLoadLignesFactures: 'loadLignesFacture',
    }),
    ...Parametres.mapActions({
      loadParametresInterne: 'loadParametresInterne',
    }),
    ...Permissions.mapActions({
      showPopUp: 'showPopup',
    }),
    getSousTypeLibelle(type) {
      return this.$t(FACTURE_SOUS_TYPE.find(st => st.code === type).libelle);
    },
    getEtatLibelle(etat) {
      return this.$t(FACTURE_ETAT.find(st => st.code === etat).libelle);
    },
    getLibelleCategorieProduit(typeProduit) {
      const typeProduitFind = CATEGORIE_PRODUIT.find((type) => {
        return type.code === typeProduit;
      });
      return typeProduitFind ? typeProduitFind.libelle : null;
    },
    debutLoading() {
      if (this.loadingComponent) this.loadingComponent.close();
      this.loadingComponent = this.$buefy.loading.open();
    },
    finLoading() {
      this.loadingComponent.close();
    },
    gestionDataLoadLignesFacture(data) {
      checkPeriodeAndFormat(data.query, 'datesPremierAffichage');
      checkPeriodeAndFormat(data.query, 'dateFacture');
      if (this.criteresRecherche.agencesId.length === 0) {
        data.query.agencesId = this.agencesSelectionnees.map(agence => agence.agenceId);
      }
      data.query.typesProduits = tagInputToArray(this.criteresRecherche.typesProduits, 'code');
      data.query.typesProduitRegroupement = tagInputToArray(this.criteresRecherche.typesProduitRegroupement, 'code');
      data.query.groupeIds = tagInputToArray(data.query.groupeIds, 'id');
      data.query.enseigneIds = tagInputToArray(data.query.enseigneIds, 'id');
      data.query.tiersId = tagInputToArray(data.query.tiersId, 'id');
      data.query.fournisseursId = tagInputToArray(data.query.fournisseursId, 'id');
      data.query.produitsIds = tagInputToArray(data.query.produitsIds, 'id');
      data.query.produitRegroupementIds = tagInputToArray(data.query.produitRegroupementIds, 'id');
      if (this.$route.name === 'c.presse.plan_media'
        || this.$route.name === 'm.presse.plan_media-edit') {
        data.query.planMediaIds = [this.$route.params.id];
      }
      data.query.societeId = this.societeActiveId;
      data.query.typesDevisCommandeFacture = [TYPE_FACTURE];
    },
    recherche() {
      this.$refs[this.constantes.REFERENCE_TABLE_LISTE_LIGNES_FACTURE].refresh();
    },
    goFirstPageAndCheckChangement() {
      if (this.criteresRecherche.agencesId.length === 0) return;
      const agencesId = [];
      this.criteresRecherche.agencesId.forEach((id) => {
        const findInAgencesSelectionnes = this.agencesSelectionnees.some((agenceSelectionne) => {
          return agenceSelectionne.agenceId === id;
        });
        if (findInAgencesSelectionnes) agencesId.push(id);
      });
      this.criteresRecherche.agencesId = agencesId;
      this.$refs[this.constantes.REFERENCE_TABLE_LISTE_LIGNES_FACTURE].setPage(1);
    },
    getRouteClient(nomId) {
      return {
        to: {
          name: 'c.bodac.clients/contacts-client',
          params: {
            id: nomId.split('#')[1],
          },
        },
        message: nomId.split('#')[0],
      };
    },
  },
  beforeDestroy() {
    if (this.lockMenu) this.setLockMenu(false);
    this.unsubscribeValue();
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      this.unsubscribeValue = await subscribeMutation(this.$store, 'societe/' + SET_DETECT_CHANGE_SOCIETE, this.goFirstPageAndCheckChangement, this.recherche);
      await this.loadParametresInterne([PARAMETRES_INTERNE_KEYS.exportLvc.key]);
      const critere = cloneDeep(this.criteresRecherche);
      if (this.produit) {
        critere.produitsIds = [this.produit];
      } else if (this.produitId) {
        const produit = (await getProduit(this.produitId)).data.result;
        critere.produitsIds = [produit];
      }
      await updateCritereWithParametreInterne(critere);
      if (this.moduleSecteurActive) {
        const secteurCurrentUser = await getSecteursIdByUser(COMMERCIAL);
        if (secteurCurrentUser.length === 0
          && this.criteresRecherche.secteurIds.length > 0) {
          const secteurs = await getSecteursByIds(this.criteresRecherche.secteurIds, COMMERCIAL);
          critere.secteurIds = secteurs.map(secteur => secteur.id);
        } else {
          critere.secteurIds = secteurCurrentUser.map(secteur => secteur.id) ?? [];
        }
      }
      this.setCritereRecherche(critere);
      this.mountedLoadingEnd = true;
      this.setInformationPage(this.$t('pageGescom.lignesFacture.liste'));
      loadingComponent.close();
    } catch (e) {
      messageErreur(this.$i18n);
      loadingComponent.close();
      throw e;
    }
  },
};
</script>
